import React, { useState, useEffect } from 'react';
import { variableOverheadAPI } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatPercentage } from '../../utils/calculations';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Save, X as XIcon, AlertCircle, TrendingUp, BarChart3, Plus } from 'lucide-react';
import Modal from '../Layout/Modal.jsx';

// Item Row Component for inline editing
const ItemRow = ({ item, onUpdate, onDelete, loading, t, i18n }) => {
  const { formatCurrency } = useCurrency();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    name_en: item.name_en || '',
    name_ar: item.name_ar || '',
    monthly_value: item.monthly_value?.toString() || '',
    yearly_value: item.yearly_value?.toString() || ''
  });

  const handleSave = async () => {
    const itemData = {
      name_en: editValues.name_en,
      name_ar: editValues.name_ar,
      monthly_value: editValues.monthly_value ? parseFloat(editValues.monthly_value) : null,
      yearly_value: editValues.yearly_value ? parseFloat(editValues.yearly_value) : null
    };
    await onUpdate(item.id, itemData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValues({
      name_en: item.name_en || '',
      name_ar: item.name_ar || '',
      monthly_value: item.monthly_value?.toString() || '',
      yearly_value: item.yearly_value?.toString() || ''
    });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <tr className="bg-primary/10">
        <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-card-foreground">{item.id}</td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="text"
            required
            value={editValues.name_en}
            onChange={(e) => setEditValues({ ...editValues, name_en: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
            placeholder={t('allocatedVariableCosts.itemNameEnPlaceholder')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="text"
            value={editValues.name_ar}
            onChange={(e) => setEditValues({ ...editValues, name_ar: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
            placeholder={t('allocatedVariableCosts.itemNameArPlaceholder')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="number"
            step="0.01"
            value={editValues.monthly_value}
            onChange={(e) => {
              const monthly = e.target.value;
              setEditValues({
                ...editValues,
                monthly_value: monthly,
                yearly_value: monthly ? (parseFloat(monthly) * 12).toString() : ''
              });
            }}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
            placeholder={t('allocatedVariableCosts.monthlyValuePlaceholder')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="number"
            step="0.01"
            value={editValues.yearly_value}
            onChange={(e) => {
              const yearly = e.target.value;
              setEditValues({
                ...editValues,
                yearly_value: yearly,
                monthly_value: yearly ? (parseFloat(yearly) / 12).toString() : ''
              });
            }}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
            placeholder={t('allocatedVariableCosts.yearlyValuePlaceholder')}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="inline-flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
              title={t('allocatedVariableCosts.save')}
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
              title={t('allocatedVariableCosts.cancel')}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-muted/50">
      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-card-foreground">{item.id}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="font-medium text-card-foreground">{item.name_en}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="font-medium text-card-foreground">{item.name_ar || '-'}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">{formatCurrency(item.monthly_value || 0)}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">{formatCurrency(item.yearly_value || 0)}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="inline-flex items-center gap-2">
          <button
            onClick={handleEdit}
            disabled={loading}
            className={`inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
          >
            {t('allocatedVariableCosts.edit')}
          </button>
          <button
            onClick={() => onDelete(item.id)}
            disabled={loading}
            className={`inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-colors disabled:opacity-50 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
          >
            {t('allocatedVariableCosts.deleteItem')}
          </button>
        </div>
      </td>
    </tr>
  );
};

// Distribution Row Component for inline editing
const DistributionRow = ({ item, onUpdate, onDelete, loading, summary, t, i18n }) => {
  const { formatCurrency } = useCurrency();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    allocated_percentage: item.allocated_percentage || 0,
    expected_orders: item.expected_orders || 0
  });

  const resetEditValues = () => {
    setEditValues({
      allocated_percentage: item.allocated_percentage || 0,
      expected_orders: item.expected_orders || 0
    });
  };

  const handleSave = async () => {
    const saved = await onUpdate(item.category_id, editValues.allocated_percentage, editValues.expected_orders);
    if (!saved) {
      resetEditValues();
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    resetEditValues();
    setIsEditing(false);
  };

  const handleEdit = () => {
    resetEditValues();
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <tr className="bg-primary/10">
        <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-card-foreground">{item.category_id}</td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div>
            {i18n.language === 'ar' ? (
              <>
                <div className="font-medium text-card-foreground">{item.category_name_ar || item.category_name_en || 'Unknown'}</div>
                <div className="text-muted-foreground">{item.category_name_en || ''}</div>
              </>
            ) : (
              <>
                <div className="font-medium text-card-foreground">{item.category_name_en || 'Unknown'}</div>
                <div className="text-muted-foreground">{item.category_name_ar || ''}</div>
              </>
            )}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={editValues.allocated_percentage}
            onChange={(e) => setEditValues({ ...editValues, allocated_percentage: parseFloat(e.target.value) || 0 })}
            className="w-20 px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="number"
            min="0"
            value={editValues.expected_orders}
            onChange={(e) => setEditValues({ ...editValues, expected_orders: parseInt(e.target.value) || 0 })}
            className="w-24 px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">
          {editValues.expected_orders > 0 
            ? formatCurrency(((summary?.total_monthly_variable_costs || 0) * (editValues.allocated_percentage / 100)) / editValues.expected_orders)
            : formatCurrency(0)
          }
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="inline-flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
              title={t('allocatedVariableCosts.save')}
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
              title={t('allocatedVariableCosts.cancel')}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-muted/50">
      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-card-foreground">{item.category_id}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          {i18n.language === 'ar' ? (
            <>
              <div className="font-medium text-card-foreground">{item.category_name_ar || item.category_name_en || 'Unknown'}</div>
              <div className="text-muted-foreground">{item.category_name_en || ''}</div>
            </>
          ) : (
            <>
              <div className="font-medium text-card-foreground">{item.category_name_en || 'Unknown'}</div>
              <div className="text-muted-foreground">{item.category_name_ar || ''}</div>
            </>
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">{formatPercentage(item.allocated_percentage || 0)}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">{item.expected_orders || 0}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-card-foreground">{formatCurrency(item.cost_per_dish || 0)}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="inline-flex items-center gap-2">
          <button
            onClick={handleEdit}
            disabled={loading}
            className={`inline-flex items-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
          >
            {t('allocatedVariableCosts.edit')}
          </button>
        </div>
      </td>
    </tr>
  );
};

const VariableOverheadCosts = () => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [distributionSearchTerm, setDistributionSearchTerm] = useState('');
  const [distributionSortBy, setDistributionSortBy] = useState('id'); // 'id' or 'alphabetical'
  const [itemsSearchTerm, setItemsSearchTerm] = useState('');
  const [itemsSortBy, setItemsSortBy] = useState('id'); // 'id' or 'alphabetical'

  // Modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemName, setDeleteItemName] = useState('');
  const [showDistributionDeleteModal, setShowDistributionDeleteModal] = useState(false);
  const [deleteDistributionId, setDeleteDistributionId] = useState(null);
  const [deleteDistributionName, setDeleteDistributionName] = useState('');

  // Form states for adding new items
  const [showAddForm, setShowAddForm] = useState(false);
  const [itemForm, setItemForm] = useState({
    name_en: '',
    name_ar: '',
    monthly_value: '',
    yearly_value: ''
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [itemsData, categoriesData, distributionData, summaryData] = await Promise.all([
        variableOverheadAPI.getItems(),
        variableOverheadAPI.getCategories(),
        variableOverheadAPI.getDistribution(),
        variableOverheadAPI.getDistributionSummary()
      ]);
      
      setItems(itemsData);
      setCategories(categoriesData);
      setDistribution(distributionData);
      setSummary(summaryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Tab 1: Variable Expense Items Functions
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const itemData = {
        name_en: itemForm.name_en,
        name_ar: itemForm.name_ar,
        monthly_value: itemForm.monthly_value ? parseFloat(itemForm.monthly_value) : null,
        yearly_value: itemForm.yearly_value ? parseFloat(itemForm.yearly_value) : null
      };

      await variableOverheadAPI.createItem(itemData);
      setItemForm({ name_en: '', name_ar: '', monthly_value: '', yearly_value: '' });
      setShowAddForm(false);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemUpdate = async (id, itemData) => {
    setLoading(true);
    setError('');

    try {
      await variableOverheadAPI.updateItem(id, itemData);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemDelete = async (id) => {
    // Convert id to number for comparison if needed
    const itemId = typeof id === 'string' ? parseInt(id, 10) : id;
    const item = items.find(item => {
      // Try both string and number comparison
      return String(item.id) === String(id) || item.id === itemId || item.id === id;
    });
    
    // Get item name with better fallback - use ID if no name available
    // Prioritize name_ar when language is Arabic, name_en when English
    let itemName = `Item #${id}`;
    if (item) {
      // Check for name_en, name_ar, or name, handling empty strings
      const nameEn = (item.name_en && item.name_en.trim()) || '';
      const nameAr = (item.name_ar && item.name_ar.trim()) || '';
      const name = (item.name && item.name.trim()) || '';
      
      // Use name_ar if language is Arabic, otherwise use name_en
      if (i18n.language === 'ar') {
        itemName = nameAr || nameEn || name || `Item #${item.id}`;
      } else {
        itemName = nameEn || nameAr || name || `Item #${item.id}`;
      }
    }
    
    setDeleteItemId(id);
    setDeleteItemName(itemName);
    setShowDeleteModal(true);
  };

  const confirmItemDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await variableOverheadAPI.deleteItem(deleteItemId);
      await loadData();
      setShowDeleteModal(false);
      setDeleteItemId(null);
      setDeleteItemName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelItemDelete = () => {
    setShowDeleteModal(false);
    setDeleteItemId(null);
    setDeleteItemName('');
  };

  // Tab 2: Distribution Functions
  const handleDistributionUpdate = async (categoryId, allocatedPercentage, expectedOrders) => {
    setLoading(true);
    setError('');

    try {
      // Calculate the total allocated percentage if this update were applied
      const currentTotal = summary?.distribution?.reduce((sum, dist) => {
        if (dist.category_id === categoryId) {
          // Use the new value for the current category
          return sum + parseFloat(allocatedPercentage);
        } else {
          // Use existing value for other categories
          return sum + (parseFloat(dist.allocated_percentage) || 0);
        }
      }, 0) || 0;

      // Check if total would exceed 100%
      if (currentTotal > 100) {
        setErrorModalMessage(
          t('allocatedVariableCosts.totalAllocatedCannotExceed', {
            total: currentTotal.toFixed(2),
          })
        );
        setShowErrorModal(true);
        return false;
      }

      const distributionData = {
        allocated_percentage: parseFloat(allocatedPercentage),
        expected_orders: parseInt(expectedOrders)
      };

      // Use the new API endpoint that handles both create and update by category_id
      await variableOverheadAPI.updateDistributionByCategory(categoryId, distributionData);

      await loadData();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleDistributionDelete = async (id) => {
    const distribution = summary?.distribution?.find(dist => dist.id === id);
    setDeleteDistributionId(id);
    setDeleteDistributionName(distribution?.category_name_en || 'this distribution');
    setShowDistributionDeleteModal(true);
  };

  const confirmDistributionDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await variableOverheadAPI.deleteDistribution(deleteDistributionId);
      await loadData();
      setShowDistributionDeleteModal(false);
      setDeleteDistributionId(null);
      setDeleteDistributionName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelDistributionDelete = () => {
    setShowDistributionDeleteModal(false);
    setDeleteDistributionId(null);
    setDeleteDistributionName('');
  };

  // Calculate totals for Tab 1
  const totalMonthly = items.reduce((sum, item) => sum + (parseFloat(item.monthly_value) || 0), 0);
  const totalYearly = items.reduce((sum, item) => sum + (parseFloat(item.yearly_value) || 0), 0);

  return (
    <div className="bg-background text-foreground min-h-screen p-6">
      <div className="space-y-6">
        {/* Header — same layout as Fixed overhead so RTL title aligns like توزيع التكاليف الثابتة */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('allocatedVariableCosts.title')}</h1>
            <p className="mt-2 text-muted-foreground">{t('allocatedVariableCosts.description')}</p>
          </div>

          <div className="flex justify-end mt-4 sm:mt-0">
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-foreground bg-primary border-2 border-primary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
              title="Add new item"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('allocatedVariableCosts.addNewItem')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-destructive mr-3 flex-shrink-0" />
              <p className="text-destructive">{error}</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex bg-muted rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab(0)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 0
                ? 'bg-card text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            {t('allocatedVariableCosts.enterItems')}
          </button>
          <button
            onClick={() => setActiveTab(1)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 1
                ? 'bg-card text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            {t('allocatedVariableCosts.distributeCosts')}
          </button>
        </div>

        {/* Tab 1: Enter Allocated Variable Expense Items */}
        {activeTab === 0 && (
          <div className="space-y-6">
            {/* Totals Display */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                <div className="text-center">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('allocatedVariableCosts.totalMonthlyValue')}</h4>
                  <p className="text-3xl font-bold text-primary">{formatCurrency(totalMonthly)}</p>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                <div className="text-center">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('allocatedVariableCosts.totalYearlyValue')}</h4>
                  <p className="text-3xl font-bold text-primary">{formatCurrency(totalYearly)}</p>
                </div>
              </div>
            </div>

            {/* Items Header */}
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-medium text-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>{t('allocatedVariableCosts.variableExpenseItems')}</h3>
            </div>

            {/* Items Table */}
            <div className="bg-card border border-border rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-border">
                <h3 className={`text-lg font-medium text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`} style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}>{t('allocatedVariableCosts.itemsList')}</h3>
              </div>
              
              {/* Search and Sort Bar */}
              <div className="px-6 py-4 border-b border-border">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder={t('allocatedVariableCosts.searchByItemName')}
                      value={itemsSearchTerm}
                      onChange={(e) => setItemsSearchTerm(e.target.value)}
                      className={`w-full py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground ${i18n.language === 'ar' ? 'pl-6 pr-10' : 'pl-10 pr-4'}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                    />
                    <div className={`absolute inset-y-0 flex items-center pointer-events-none ${i18n.language === 'ar' ? 'right-0 pr-3' : 'left-0 pl-3'}`}>
                      <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        setItemsSortBy(itemsSortBy === 'id' ? 'alphabetical' : 'id');
                        e.target.blur(); // Remove focus after click
                      }}
                      className="inline-flex items-center justify-center p-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors flex-shrink-0 w-10 h-10"
                      title={`${itemsSortBy === 'id' ? t('allocatedVariableCosts.sortById') : t('allocatedVariableCosts.sortByName')}`}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.id')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.itemNameEn')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.itemNameAr')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.monthlyValue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.yearlyValue')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                  {items
                    ?.filter(item => 
                      (item.name_en || '').toLowerCase().includes(itemsSearchTerm.toLowerCase()) ||
                      (item.name_ar || '').toLowerCase().includes(itemsSearchTerm.toLowerCase())
                    )
                    ?.sort((a, b) => {
                      if (itemsSortBy === 'id') {
                        return a.id - b.id;
                      } else {
                        // Sort by name (English first, then Arabic)
                        const nameA = (a.name_en || a.name_ar || '').toLowerCase();
                        const nameB = (b.name_en || b.name_ar || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      }
                    })
                    ?.map((item) => (
                      <ItemRow 
                        key={item.id}
                        item={item}
                        onUpdate={handleItemUpdate}
                        onDelete={handleItemDelete}
                        loading={loading}
                        t={t}
                        i18n={i18n}
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

        {/* Tab 2: Distribute Allocated Variable Costs by Category */}
        {activeTab === 1 && (
          <div className="space-y-6">
            {/* Total Allocation Warning */}
            {(() => {
              const total = summary?.distribution?.reduce((sum, dist) => sum + (parseFloat(dist.allocated_percentage) || 0), 0) || 0;
              if (total > 100) {
                return (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-destructive" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-destructive">
                          Total allocation exceeds 100%
                        </h3>
                        <div className="mt-2 text-sm text-destructive">
                          <p>Current total: {total.toFixed(2)}%. Please reduce allocations to stay within 100%.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Distribution Results Table */}
            <div className="bg-card border border-border rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b border-border">
                <div className="flex justify-between items-center">
                  <h3 className={`text-lg font-medium text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>{t('allocatedVariableCosts.costDistributionResults')}</h3>
                  <div className="text-sm">
                    <span className={`font-medium text-muted-foreground ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>{t('allocatedVariableCosts.totalAllocated')}: </span>
                    <span className={`font-bold ${(() => {
                      const total = summary?.distribution?.reduce((sum, dist) => sum + (parseFloat(dist.allocated_percentage) || 0), 0) || 0;
                      return total > 100 ? 'text-destructive' : total === 100 ? 'text-chart-3' : 'text-muted-foreground';
                    })()}`}>
                      {(summary?.distribution?.reduce((sum, dist) => sum + (parseFloat(dist.allocated_percentage) || 0), 0) || 0).toFixed(2)}%
                    </span>
                    <span className="text-muted-foreground"> / 100%</span>
                  </div>
                </div>
              </div>
            
              {/* Search and Sort Bar */}
              <div className="px-6 py-4 border-b border-border">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder={t('allocatedVariableCosts.searchByCategoryName')}
                      value={distributionSearchTerm}
                      onChange={(e) => setDistributionSearchTerm(e.target.value)}
                      className={`w-full py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground ${i18n.language === 'ar' ? 'pl-6 pr-10' : 'pl-10 pr-4'}`}
                      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                    />
                    <div className={`absolute inset-y-0 flex items-center pointer-events-none ${i18n.language === 'ar' ? 'right-0 pr-3' : 'left-0 pl-3'}`}>
                      <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={(e) => {
                        setDistributionSortBy(distributionSortBy === 'id' ? 'alphabetical' : 'id');
                        e.target.blur(); // Remove focus after click
                      }}
                      className="inline-flex items-center justify-center p-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors flex-shrink-0 w-10 h-10"
                      title={`${distributionSortBy === 'id' ? t('allocatedVariableCosts.sortById') : t('allocatedVariableCosts.sortByName')}`}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.categoryId')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.category')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.allocatedPercentage')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.expectedOrders')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('allocatedVariableCosts.costPerDish')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                  {summary?.distribution
                    ?.filter(item => 
                      item.category_name_en?.toLowerCase().includes(distributionSearchTerm.toLowerCase()) ||
                      item.category_name_ar?.toLowerCase().includes(distributionSearchTerm.toLowerCase())
                    )
                    ?.sort((a, b) => {
                      if (distributionSortBy === 'id') {
                        return a.category_id - b.category_id;
                      } else {
                        // Sort by name (English first, then Arabic)
                        const nameA = (a.category_name_en || a.category_name_ar || '').toLowerCase();
                        const nameB = (b.category_name_en || b.category_name_ar || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      }
                    })
                    ?.map((item) => (
                      <DistributionRow 
                        key={item.category_id}
                        item={item}
                        onUpdate={handleDistributionUpdate}
                        onDelete={handleDistributionDelete}
                        loading={loading}
                        summary={summary}
                        t={t}
                        i18n={i18n}
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


        {/* Error Modal */}
        {showErrorModal && (
          <div className="modal-overlay" onClick={() => setShowErrorModal(false)}>
            <div
              className={`modal-content w-full max-w-md mx-auto my-8 px-4 sm:px-6 lg:px-8 pt-4 pb-4 ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
              style={{
                maxHeight: '90vh',
                overflowY: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
              }}
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              <div className="flex justify-between items-center mb-4 gap-3">
                <h2 className="text-lg font-semibold text-destructive">
                  {t('allocatedVariableCosts.validationError')}
                </h2>
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="text-muted-foreground hover:text-card-foreground"
                >
                  <XIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                  </div>
                  <div
                    className="min-w-0 flex-1"
                    style={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
                  >
                    <h3 className="text-sm font-medium text-destructive">
                      {t('allocatedVariableCosts.allocationLimitExceeded')}
                    </h3>
                    <div className="mt-2 text-sm text-destructive">
                      <p style={{ textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
                        {errorModalMessage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`flex ${i18n.language === 'ar' ? 'justify-start' : 'justify-end'}`}>
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  {t('common.ok')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Item Confirmation Modal */}
        <Modal isOpen={showDeleteModal} onClose={cancelItemDelete}>
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="h-6 w-6 text-destructive shrink-0" />
            <h2 className={`text-lg font-semibold text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>{t('allocatedVariableCosts.deleteItem')}</h2>
          </div>
          <div className="mb-8">
            <p className={`text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>
              {(() => {
                const itemName = deleteItemName || 'this item';
                const baseMessage = t('allocatedVariableCosts.deleteItemConfirm', { itemName });
                // If interpolation didn't work, manually replace {itemName}
                return baseMessage.includes('{itemName}') 
                  ? baseMessage.replace('{itemName}', itemName)
                  : baseMessage;
              })()}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={cancelItemDelete}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={confirmItemDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-colors"
              disabled={loading}
            >
              {loading ? t('allocatedVariableCosts.deleting') : t('allocatedVariableCosts.deleteItem')}
            </button>
          </div>
        </Modal>

        {/* Delete Distribution Confirmation Modal */}
        <Modal isOpen={showDistributionDeleteModal} onClose={cancelDistributionDelete}>
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="h-6 w-6 text-destructive shrink-0" />
            <h2 className={`text-lg font-semibold text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>{t('allocatedVariableCosts.deleteDistribution')}</h2>
          </div>
          <div className="mb-8">
            <p className={`text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>
              {t('allocatedVariableCosts.deleteDistributionConfirm', { categoryName: deleteDistributionName })}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-3">
            <button
              onClick={cancelDistributionDelete}
              className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={confirmDistributionDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-colors"
              disabled={loading}
            >
              {loading ? t('allocatedVariableCosts.deleting') : t('allocatedVariableCosts.deleteDistribution')}
            </button>
          </div>
        </Modal>

        {/* Add Item Modal */}
        <Modal isOpen={showAddForm} onClose={() => setShowAddForm(false)}>
          <div className={`flex items-center gap-3 mb-6 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <Plus className="h-6 w-6 text-primary shrink-0" />
            <h2 className={`text-lg font-semibold text-card-foreground ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`}>{t('allocatedVariableCosts.editItem')}</h2>
          </div>
          <form onSubmit={handleItemSubmit} className={`space-y-4 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <div>
              <label className={`block text-sm font-medium text-card-foreground mb-2 ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`} style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}>{t('allocatedVariableCosts.itemNameEn')}</label>
              <input
                type="text"
                required
                value={itemForm.name_en}
                onChange={(e) => setItemForm({ ...itemForm, name_en: e.target.value })}
                className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder={t('allocatedVariableCosts.itemNameEnPlaceholder')}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-card-foreground mb-2 ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`} style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}>{t('allocatedVariableCosts.itemNameAr')}</label>
              <input
                type="text"
                value={itemForm.name_ar}
                onChange={(e) => setItemForm({ ...itemForm, name_ar: e.target.value })}
                className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                placeholder={t('allocatedVariableCosts.itemNameArPlaceholder')}
                dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium text-card-foreground mb-2 ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`} style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}>{t('allocatedVariableCosts.monthlyValue')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={itemForm.monthly_value}
                  onChange={(e) => {
                    const monthly = e.target.value;
                    setItemForm({
                      ...itemForm,
                      monthly_value: monthly,
                      yearly_value: monthly ? (parseFloat(monthly) * 12).toString() : ''
                    });
                  }}
                  className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                  placeholder={t('allocatedVariableCosts.monthlyValuePlaceholder')}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium text-card-foreground mb-2 ${i18n.language === 'ar' ? 'font-arabic text-right' : 'text-left'}`} style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}>{t('allocatedVariableCosts.yearlyValue')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={itemForm.yearly_value}
                  onChange={(e) => {
                    const yearly = e.target.value;
                    setItemForm({
                      ...itemForm,
                      yearly_value: yearly,
                      monthly_value: yearly ? (parseFloat(yearly) / 12).toString() : ''
                    });
                  }}
                  className={`w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`}
                  placeholder={t('allocatedVariableCosts.yearlyValuePlaceholder')}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  style={i18n.language === 'ar' ? { textAlign: 'right' } : { textAlign: 'left' }}
                />
              </div>
            </div>
            <div className={`flex flex-wrap items-center gap-3 ${i18n.language === 'ar' ? 'justify-start flex-row-reverse' : 'justify-end'}`}>
              {i18n.language === 'ar' ? (
                <>
                  <button
                    type="submit"
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
                    disabled={loading}
                  >
                    {loading ? t('allocatedVariableCosts.adding') : t('allocatedVariableCosts.addItem')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
                    disabled={loading}
                  >
                    {t('allocatedVariableCosts.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className={`inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
                    disabled={loading}
                  >
                    {t('allocatedVariableCosts.cancel')}
                  </button>
                  <button
                    type="submit"
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors ${i18n.language === 'ar' ? 'font-arabic' : ''}`}
                    disabled={loading}
                  >
                    {loading ? t('allocatedVariableCosts.adding') : t('allocatedVariableCosts.addItem')}
                  </button>
                </>
              )}
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default VariableOverheadCosts;


